/**
 * Every surface in the game, drawn into a `<canvas>` at scene-build time.
 *
 * Procedural rather than loaded, for reasons that are about the shape of this
 * project rather than about taste:
 *
 *  - There is no asset pipeline and adding one is not free. `index.html` inlines
 *    its own favicon as a data URI specifically to keep the client a single
 *    served file, and the two deploy targets (a Worker assets binding and
 *    Firebase Hosting) disagree about cache headers for anything under
 *    `/assets/` that Vite did not content-hash. A texture that ships as a file
 *    is a texture that can go stale in someone's browser.
 *  - `GameRenderer` is constructed *after* the handshake names the level, and
 *    draws on the next frame. Anything asynchronous here would mean a first
 *    frame with the level untextured, which is the frame a player joins on.
 *
 * Everything is seeded (see `rng`) so a given texture is identical on every
 * machine and every run. That is not a correctness requirement — nothing hashes
 * these — but a renderer whose output moves between runs cannot be compared
 * against a screenshot, and the README's rendering claims are all screenshot
 * claims.
 *
 * Sizes are powers of two so `generateMipmaps` gets a full chain. Colour maps
 * must be tagged `SRGBColorSpace` by the caller (see materials.ts); the decode
 * is done by the sampler and works whether the texture is bound as
 * `material.map` or as a uniform of our own.
 *
 * Textures are memoized per parameter set to avoid regeneration on reconnect.
 */

/** Cache of generated textures keyed by parameters to avoid regeneration. */
const textureCache = new Map<string, HTMLCanvasElement>();

/** Clear the texture cache when starting a new game. */
export function clearTextureCache(): void {
  textureCache.clear();
}

/**
 * Mulberry32. Small, seeded and dependency-free — the same reasoning
 * `mapfmt`'s FNV choice records: this is asked to be repeatable, not random.
 */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A drawing surface plus its 2D context, sized and cleared. */
function surface(size: number, height = size): CanvasRenderingContext2D {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("2D canvas context unavailable");
  return ctx;
}

/**
 * Fine per-pixel grain, composited over whatever is already drawn.
 *
 * This is what stops a flat fill reading as plastic. It survives mipmapping as
 * a slight roughness rather than as visible noise, which is the intent — the
 * texture should be felt at 2m and invisible at 20m.
 */
function grain(ctx: CanvasRenderingContext2D, seed: number, amount: number): void {
  const { width, height } = ctx.canvas;
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  const random = rng(seed);
  for (let i = 0; i < data.length; i += 4) {
    const n = (random() - 0.5) * amount;
    data[i] = clamp255(data[i]! + n);
    data[i + 1] = clamp255(data[i + 1]! + n);
    data[i + 2] = clamp255(data[i + 2]! + n);
  }
  ctx.putImageData(image, 0, 0);
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/** Memoize a texture generator by its parameters to avoid regeneration on reconnect. */
function memoized(
  key: string,
  generator: () => HTMLCanvasElement,
): HTMLCanvasElement {
  let cached = textureCache.get(key);
  if (cached === undefined) {
    cached = generator();
    textureCache.set(key, cached);
  }
  return cached;
}

/** Soft low-frequency mottling — dye lots in carpet, damp in concrete. */
function mottle(
  ctx: CanvasRenderingContext2D,
  seed: number,
  count: number,
  radius: number,
  color: string,
): void {
  const { width, height } = ctx.canvas;
  const random = rng(seed);
  ctx.save();
  for (let i = 0; i < count; i++) {
    const x = random() * width;
    const y = random() * height;
    const r = radius * (0.5 + random());
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Floors
// ---------------------------------------------------------------------------

/**
 * Office carpet tile: a 4x4 grid of tiles laid at alternating grain, which is
 * how the real thing is laid and the only reason it reads as carpet rather
 * than as a green field. One texture spans `CARPET_METRES`.
 */
export function carpetTexture(base: string, seam: string): HTMLCanvasElement {
  return memoized(`carpet:${base}:${seam}`, () => {
    const size = 512;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

  const tile = size / 4;
  const random = rng(0x0ca7be7);
  for (let ty = 0; ty < 4; ty++) {
    for (let tx = 0; tx < 4; tx++) {
      // Alternating tile direction reads as a subtle checker under raking
      // light, which is exactly what a carpet-tiled floor does.
      ctx.save();
      ctx.beginPath();
      ctx.rect(tx * tile, ty * tile, tile, tile);
      ctx.clip();
      ctx.fillStyle = (tx + ty) % 2 === 0 ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.03)";
      ctx.fillRect(tx * tile, ty * tile, tile, tile);
      // Loop pile: short strokes along the tile's own direction.
      ctx.strokeStyle = "rgba(0,0,0,0.05)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 240; i++) {
        const x = tx * tile + random() * tile;
        const y = ty * tile + random() * tile;
        const len = 2 + random() * 4;
        ctx.beginPath();
        ctx.moveTo(x, y);
        if ((tx + ty) % 2 === 0) ctx.lineTo(x + len, y);
        else ctx.lineTo(x, y + len);
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  // Tile seams, drawn last so nothing paints over them.
  ctx.strokeStyle = seam;
  ctx.lineWidth = 1;
  for (let i = 1; i < 4; i++) {
    ctx.beginPath();
    ctx.moveTo(i * tile + 0.5, 0);
    ctx.lineTo(i * tile + 0.5, size);
    ctx.moveTo(0, i * tile + 0.5);
    ctx.lineTo(size, i * tile + 0.5);
    ctx.stroke();
  }

    mottle(ctx, 0xca47, 26, 60, "rgba(0,0,0,0.05)");
    grain(ctx, 0xca4901, 26);
    return ctx.canvas;
  });
}

/** Poured concrete: depot's floor, and the fallback theme's. */
export function concreteTexture(base: string): HTMLCanvasElement {
  return memoized(`concrete:${base}`, () => {
    const size = 512;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);
    mottle(ctx, 0xc0c7, 40, 90, "rgba(255,255,255,0.03)");
    mottle(ctx, 0xc0c8, 30, 70, "rgba(0,0,0,0.06)");

    // Control joints on a 1/2-texture pitch, and a scatter of aggregate.
    ctx.strokeStyle = "rgba(0,0,0,0.22)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(size / 2, 0);
    ctx.lineTo(size / 2, size);
    ctx.moveTo(0, size / 2);
    ctx.lineTo(size, size / 2);
    ctx.stroke();

    const random = rng(0xa66);
    for (let i = 0; i < 900; i++) {
      ctx.fillStyle = random() > 0.5 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.07)";
      ctx.fillRect(random() * size, random() * size, 1 + random() * 2, 1 + random() * 2);
    }
    grain(ctx, 0xc04c, 18);
    return ctx.canvas;
  });
}

// ---------------------------------------------------------------------------
// Walls
// ---------------------------------------------------------------------------

/**
 * A wall, top to bottom, authored for one specific storey height.
 *
 * This is the texture the world-space projection in materials.ts pays for.
 * Because V is world Y in metres and `wrapT` is clamped, this image is not a
 * tiling swatch — it is an elevation. The skirting board at the bottom and the
 * trim at the top land at the right height on *every* wall in the level, on a
 * wall run merged from three rects as readily as on a pillar, without a single
 * box of trim geometry existing.
 *
 * `height` is the level's own wall height, read back out of the geometry by
 * `decor.ceilingHeight` — 3m on office, 4m on depot.
 */
export function wallTexture(
  height: number,
  paint: string,
  skirting: string,
  trim: string,
): HTMLCanvasElement {
  return memoized(`wall:${height}:${paint}:${skirting}:${trim}`, () => {
    const width = 256;
    // ~85 px per metre, rounded to a power of two so mipmaps are clean.
    const size = height >= 3.5 ? 512 : 256;
    const ctx = surface(width, size);

    // V=0 is the floor and V=1 is the ceiling, so the image is drawn bottom-up:
    // canvas y grows downward, world Y grows upward.
    const metresToPx = (m: number) => size - (m / height) * size;

    ctx.fillStyle = paint;
    ctx.fillRect(0, 0, width, size);
    mottle(ctx, 0x3a11, 18, 70, "rgba(255,255,255,0.04)");
    mottle(ctx, 0x3a12, 14, 90, "rgba(0,0,0,0.04)");

    // Scuffs, concentrated low where trolleys and shoulders actually hit.
    const random = rng(0x5c0ff);
    for (let i = 0; i < 90; i++) {
      const y = metresToPx(random() * random() * height);
      ctx.fillStyle = "rgba(0,0,0,0.05)";
      ctx.fillRect(random() * width, y, 4 + random() * 26, 1 + random() * 2);
    }

    // Skirting board: 0.12m of it, with a highlight along its top edge.
    const skirtTop = metresToPx(0.12);
    ctx.fillStyle = skirting;
    ctx.fillRect(0, skirtTop, width, size - skirtTop);
    ctx.fillStyle = "rgba(255,255,255,0.10)";
    ctx.fillRect(0, skirtTop, width, 2);

    // Picture rail near the ceiling, and a darker reveal in the last 60mm so the
    // wall/ceiling junction reads as a junction rather than as a colour change.
    const railY = metresToPx(height - 0.35);
    ctx.fillStyle = trim;
    ctx.fillRect(0, railY, width, 3);
    ctx.fillStyle = "rgba(0,0,0,0.18)";
    ctx.fillRect(0, 0, width, Math.max(2, (0.06 / height) * size));

    // A hint of ambient occlusion where the wall meets the floor. Cheap, and it
    // does more for the sense of enclosure than any of the above.
    const shade = ctx.createLinearGradient(0, size, 0, metresToPx(0.5));
    shade.addColorStop(0, "rgba(0,0,0,0.25)");
    shade.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = shade;
    ctx.fillRect(0, metresToPx(0.5), width, size - metresToPx(0.5));

    grain(ctx, 0x3a13, 12);
    return ctx.canvas;
  });
}

/** Painted steel: the fallback theme's walls, and depot's shelving. */
export function panelTexture(base: string, line: string): HTMLCanvasElement {
  return memoized(`panel:${base}:${line}`, () => {
    const size = 256;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

    // Ribbed panel: vertical stiffeners every eighth.
    const step = size / 8;
    for (let i = 0; i < 8; i++) {
      const x = i * step;
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      ctx.fillRect(x, 0, 2, size);
      ctx.fillStyle = line;
      ctx.fillRect(x + step - 2, 0, 2, size);
    }
    mottle(ctx, 0x9a11, 12, 60, "rgba(0,0,0,0.05)");
    grain(ctx, 0x9a12, 14);
    return ctx.canvas;
  });
}

// ---------------------------------------------------------------------------
// Ceiling
// ---------------------------------------------------------------------------

/** Suspended ceiling: mineral-fibre tiles in a visible grid, 4 to a texture. */
export function ceilingTexture(tile: string, grid: string): HTMLCanvasElement {
  return memoized(`ceiling:${tile}:${grid}`, () => {
    const size = 512;
    const ctx = surface(size);
    ctx.fillStyle = tile;
    ctx.fillRect(0, 0, size, size);

    const cell = size / 4;
    const random = rng(0xce111);
    // Perforations. The reason a ceiling tile reads as a ceiling tile.
    ctx.fillStyle = "rgba(0,0,0,0.10)";
    for (let i = 0; i < 5000; i++) {
      ctx.fillRect(random() * size, random() * size, 1.5, 1.5);
    }

    // The T-bar grid the tiles sit in, with a lit top edge.
    for (let i = 0; i <= 4; i++) {
      const p = i * cell;
      ctx.fillStyle = grid;
      ctx.fillRect(p - 2, 0, 4, size);
      ctx.fillRect(0, p - 2, size, 4);
      ctx.fillStyle = "rgba(255,255,255,0.10)";
      ctx.fillRect(p - 2, 0, 1, size);
      ctx.fillRect(0, p - 2, size, 1);
    }
    mottle(ctx, 0xce112, 16, 70, "rgba(0,0,0,0.04)");
    grain(ctx, 0xce113, 10);
    return ctx.canvas;
  });
}

// ---------------------------------------------------------------------------
// Furniture and props
// ---------------------------------------------------------------------------

/** Desk laminate: the top face of a partition run. */
export function laminateTexture(base: string): HTMLCanvasElement {
  return memoized(`laminate:${base}`, () => {
    const size = 256;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

    // Faint woodgrain, stretched far along one axis so it reads as a veneer
    // rather than as sawn timber.
    const random = rng(0x1a3);
    ctx.strokeStyle = "rgba(0,0,0,0.05)";
    ctx.lineWidth = 1;
    for (let i = 0; i < 90; i++) {
      const y = random() * size;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.bezierCurveTo(size / 3, y + (random() - 0.5) * 8, (size * 2) / 3, y + (random() - 0.5) * 8, size, y);
      ctx.stroke();
    }
    grain(ctx, 0x1a4, 10);
    return ctx.canvas;
  });
}

/** Partition fabric: the side face of a desk run, and a chair's upholstery. */
export function fabricTexture(base: string): HTMLCanvasElement {
  return memoized(`fabric:${base}`, () => {
    const size = 256;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

    // A plain weave: alternating warp and weft at 4px, which at this texel
    // density is a felt rather than a visible check.
    for (let y = 0; y < size; y += 4) {
      for (let x = 0; x < size; x += 4) {
        ctx.fillStyle = ((x + y) / 4) % 2 === 0 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)";
        ctx.fillRect(x, y, 4, 4);
      }
    }
    mottle(ctx, 0xfab1, 14, 50, "rgba(0,0,0,0.05)");
    grain(ctx, 0xfab2, 18);
    return ctx.canvas;
  });
}

/** Sawn timber: crates and pallets. Planks run along the U axis. */
export function woodTexture(base: string, gap: string): HTMLCanvasElement {
  return memoized(`wood:${base}:${gap}`, () => {
    const size = 256;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

    const planks = 4;
    const step = size / planks;
    const random = rng(0x0d00);
    for (let i = 0; i < planks; i++) {
      const y = i * step;
      // Each plank a slightly different cut.
      ctx.fillStyle = `rgba(${random() > 0.5 ? "255,255,255" : "0,0,0"},0.04)`;
      ctx.fillRect(0, y, size, step);
      // Grain.
      ctx.strokeStyle = "rgba(0,0,0,0.10)";
      ctx.lineWidth = 1;
      for (let g = 0; g < 14; g++) {
        const gy = y + random() * step;
        ctx.beginPath();
        ctx.moveTo(0, gy);
        ctx.bezierCurveTo(size / 3, gy + (random() - 0.5) * 6, (size * 2) / 3, gy + (random() - 0.5) * 6, size, gy);
        ctx.stroke();
      }
      // The shadowed gap between planks.
      ctx.fillStyle = gap;
      ctx.fillRect(0, y, size, 2);
    }
    grain(ctx, 0x0d01, 16);
    return ctx.canvas;
  });
}

/** Painted or galvanised metal: barrels, cabinets, printers, coolers. */
export function metalTexture(base: string): HTMLCanvasElement {
  return memoized(`metal:${base}`, () => {
    const size = 256;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

    // Brushed: fine horizontal streaks, plus a few chips down to bare metal.
    const random = rng(0x3e7a1);
    ctx.strokeStyle = "rgba(255,255,255,0.045)";
    for (let i = 0; i < 300; i++) {
      const y = random() * size;
      const x = random() * size;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 10 + random() * 50, y);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(0,0,0,0.13)";
    for (let i = 0; i < 40; i++) {
      ctx.fillRect(random() * size, random() * size, 1 + random() * 3, 1 + random() * 3);
    }
    mottle(ctx, 0x3e7a2, 10, 60, "rgba(255,255,255,0.04)");
    grain(ctx, 0x3e7a3, 12);
    return ctx.canvas;
  });
}

/** Moulded plastic: cones, and a chair's shell. Cleaner than metal. */
export function plasticTexture(base: string): HTMLCanvasElement {
  return memoized(`plastic:${base}`, () => {
    const size = 128;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);
    mottle(ctx, 0x9137, 8, 40, "rgba(255,255,255,0.05)");
    grain(ctx, 0x9138, 8);
    return ctx.canvas;
  });
}

/** Leaves: a scatter of overlapping blades for a potted plant's canopy. */
export function foliageTexture(base: string): HTMLCanvasElement {
  return memoized(`foliage:${base}`, () => {
    const size = 256;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

    const random = rng(0xf0117);
    for (let i = 0; i < 420; i++) {
      const x = random() * size;
      const y = random() * size;
      const len = 10 + random() * 26;
      const angle = random() * Math.PI * 2;
      // Lighter towards the tips, which is what makes a mass of leaves read as
      // a mass rather than as a flat green.
      const lift = random();
      ctx.strokeStyle = `rgba(${lift > 0.6 ? "210,240,190" : "20,50,25"},${0.10 + lift * 0.2})`;
      ctx.lineWidth = 1 + random() * 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
      ctx.stroke();
    }
    grain(ctx, 0xf0118, 14);
    return ctx.canvas;
  });
}

/** Terracotta: a plant pot. */
export function ceramicTexture(base: string): HTMLCanvasElement {
  return memoized(`ceramic:${base}`, () => {
    const size = 128;
    const ctx = surface(size);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);
    mottle(ctx, 0xce2a, 10, 40, "rgba(0,0,0,0.07)");
    grain(ctx, 0xce2b, 16);
    return ctx.canvas;
  });
}
